import { useFormat, useLocale, useTranslation } from '@dreamstack/i18n'
import type { GetNewsArticlePreviewsQueryResult } from '@dreamstack/investors-graphql'
import Image from 'next/legacy/image'
import type { FunctionComponent } from 'react'
import 'twin.macro'
import type { NewsType } from '../index'
import {
  HeadingH2,
  StyledNewsDate,
  StyledNewsType,
  StyledNewsTypeWrapper,
  TransparentGradientBottomToTop,
} from '../index'
import { ContinueReadingLink } from './ContinueReadingLink'
import { NewsHubPreview } from './NewsHubPreview'
import { getNewsPath } from './getNewsPath'

// TODO: Exchange with actual fallback img.
const fallbackImageUrl =
  'https://images.unsplash.com/photo-1582407947304-fd86f028f716?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=1459&q=80'

export const FirstArticle: FunctionComponent<
  React.PropsWithChildren<{
    article: NonNullable<
      NonNullable<GetNewsArticlePreviewsQueryResult['data']>['newsArticles']
    >['items'][0]
  }>
> = ({ article }) => {
  const formatDate = useFormat()
  const t = useTranslation()
  const locale = useLocale()
  if (!article) return null
  const { image, date, title } = article
  const slug = article.slug
  const newsType = article.newsType as NewsType

  if (!slug || !newsType) {
    // should never happen
    return null
  }

  const formattedDate = formatDate(date, 'dd. MMMM yyyy')
  const href = getNewsPath({ locale, slug, newsType })
  const translatedNewsType = t(`accentro:newsTypes.${newsType}`)

  return (
    <div tw="mt-px-32 lg:(mt-px-48)">
      <div tw="relative h-px-274 md:(h-px-576) lg:(mt-px-96 h-px-576) items-end hidden md:flex">
        <Image
          tw="object-cover"
          src={image?.url ?? fallbackImageUrl}
          layout="fill"
          alt={image?.description || 'Newsarticle image'}
        />
        <TransparentGradientBottomToTop />
        <div tw="p-px-64 z-10">
          <div tw="flex items-center space-x-px-24">
            <StyledNewsTypeWrapper>
              <StyledNewsType>{translatedNewsType}</StyledNewsType>
            </StyledNewsTypeWrapper>
            {date && !article.hideDate && (
              <StyledNewsDate
                light
              >{`Berlin, ${formattedDate}`}</StyledNewsDate>
            )}
          </div>
          <HeadingH2 tw="mt-px-32 text-accentroWhite-full">{title}</HeadingH2>
          <ContinueReadingLink whiteText href={href} file={article.file} />
        </div>
      </div>
      <div tw="md:hidden">
        <NewsHubPreview article={article} />
      </div>
    </div>
  )
}
